import type { CSSProperties, KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  PopoverPlacement,
  PopoverTriggerProps,
  UsePopoverPanelParams,
  UsePopoverPanelReturn,
  UsePopoverParams,
  UsePopoverReturn,
} from './types';

const ARROW_DOWN_KEY = 'ArrowDown';
const ESCAPE_KEY = 'Escape';
const TAB_KEY = 'Tab';

/**
 * Estilos del `Grow` por lado del `placement` resuelto: el panel crece desde
 * el borde anclado al trigger (MUI recipe), no siempre desde arriba. Objetos
 * a nivel de módulo para identidad estable entre renders.
 */
const GROW_STYLES: Record<'bottom' | 'left' | 'right' | 'top', CSSProperties> = {
  bottom: { transformOrigin: 'center top' },
  left: { transformOrigin: 'right center' },
  right: { transformOrigin: 'left center' },
  top: { transformOrigin: 'center bottom' },
};

function getGrowStyle(placement: PopoverPlacement = 'bottom'): CSSProperties {
  const side = placement.split('-')[0] as keyof typeof GROW_STYLES;
  return GROW_STYLES[side] ?? GROW_STYLES.bottom;
}

/**
 * Indica si el usuario prefiere movimiento reducido. La transición `Grow` de
 * MUI anima con estilos inline (JS), así que la media query CSS no basta: hay
 * que anular el `timeout` de la transición desde JS.
 */
function prefersReducedMotion(): boolean {
  return Boolean(globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}

const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

/**
 * Contenedor que la trampa de foco del diálogo modal de MUI (y del `Dialog` v2, que lo envuelve) usa
 * como raíz. Un panel montado fuera de él pierde el foco al abrirse, y un Escape dentro del árbol del
 * diálogo lo cierra entero (TAN-11141).
 */
const MUI_DIALOG_CONTAINER_SELECTOR = '.MuiDialog-container';

function getDialogContainer(anchorEl: UsePopoverPanelParams['anchorEl']): HTMLElement | undefined {
  if (!(anchorEl instanceof HTMLElement)) return undefined;
  return anchorEl.closest<HTMLElement>(MUI_DIALOG_CONTAINER_SELECTOR) ?? undefined;
}

/**
 * Indica si un `Tab`/`Shift+Tab` sacaría el foco del panel: no hay más elementos tabulables en esa
 * dirección. Un panel con campos (formulario) sigue navegando entre ellos sin cerrarse.
 */
function isTabLeavingPanel(panel: HTMLElement, target: EventTarget, isBackward: boolean): boolean {
  const tabbables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex >= 0 && !element.hasAttribute('disabled'),
  );
  if (tabbables.length === 0) return true;
  if (isBackward) return target === panel || target === tabbables[0];
  return target === tabbables.at(-1);
}

/**
 * Devuelve el foco al elemento ancla si es un `HTMLElement`. Se usa en los dos
 * cierres "intencionados" del usuario: con Escape (panel) y por selección desde
 * el contenido (`usePopover().close()`). Según el patrón WAI-ARIA APG, el cierre
 * por click fuera NO debe devolver el foco al trigger (robaría el foco al
 * elemento que el usuario acaba de pulsar), por eso `onClickAway` no lo llama.
 *
 * Si el ancla no es enfocable (consumidores que anclan al `div` que envuelve al
 * botón, como `ButtonFixedDropdown` o `ActionButtonDropdownMenu` — `.focus()` en
 * un div sin tabindex es un no-op y el foco caería a `body`), se enfoca su
 * primer descendiente enfocable (el `<button>` real del disparador).
 */
function focusAnchor(anchorEl: UsePopoverPanelParams['anchorEl']): void {
  if (!(anchorEl instanceof HTMLElement)) return;
  const target = anchorEl.matches(FOCUSABLE_SELECTOR) ? anchorEl : anchorEl.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  (target ?? anchorEl).focus();
}

/**
 * Pila de handlers de Escape de los Popover abiertos. Un único listener en
 * `document` (registrado con el primer Popover, retirado con el último) delega
 * SIEMPRE en el handler del tope de la pila — el panel montado más recientemente,
 * que es el visualmente superior. Así un Escape cierra solo el Popover superior
 * (semántica WAI-ARIA APG para paneles anidados), no todos los abiertos a la vez.
 */
const escapeHandlerStack: Array<() => void> = [];

function closeTopmostPanel(event: Pick<KeyboardEvent, 'stopPropagation'>): void {
  const topmost = escapeHandlerStack.at(-1);
  if (!topmost) return;
  event.stopPropagation();
  topmost();
}

function handleDocumentEscape(event: KeyboardEvent): void {
  if (event.key !== ESCAPE_KEY) return;
  closeTopmostPanel(event);
}

function pushEscapeHandler(handler: () => void): () => void {
  if (escapeHandlerStack.length === 0) {
    document.addEventListener('keydown', handleDocumentEscape);
  }
  escapeHandlerStack.push(handler);

  return () => {
    const index = escapeHandlerStack.lastIndexOf(handler);
    if (index !== -1) {
      escapeHandlerStack.splice(index, 1);
    }
    if (escapeHandlerStack.length === 0) {
      document.removeEventListener('keydown', handleDocumentEscape);
    }
  };
}

/**
 * Lógica interna del panel. La consume `Popover.tsx`; no forma parte de la
 * API pública (no se re-exporta desde `src/ui/index.ts`).
 *
 * Comportamiento de accesibilidad a nivel de panel:
 *  - Al abrirse (si `shouldFocusOnOpen`, default true), el foco se mueve al
 *    contenedor del panel (`tabindex="-1"`): el portal de Popper deja el panel
 *    al final de `body`, fuera del orden de tabulación tras el trigger.
 *  - Si el ancla está dentro de un diálogo modal (`.MuiDialog-container`), el
 *    panel se monta en ese contenedor, no en `body`: así la trampa de foco del
 *    diálogo no le quita el foco. Dentro del diálogo, Escape se resuelve en el
 *    propio panel (cierra solo el panel y devuelve el foco al ancla) porque el
 *    `onKeyDown` del diálogo lo recibiría antes que el listener de `document`; y
 *    un `Tab`/`Shift+Tab` que sacaría el foco del panel lo cierra y devuelve el
 *    foco al ancla (con `Shift+Tab` se frena el movimiento nativo), porque el
 *    panel queda al final del contenedor y el Tab nativo saltaría al pie del diálogo.
 *  - Mientras `open`, se registra un handler de Escape en una pila compartida
 *    (un único listener en `document`): un Escape cierra solo el panel superior
 *    y devuelve el foco al `anchorEl`, sin cerrar los Popover anidados por debajo.
 *  - `Tab`/`Shift+Tab` con el foco dentro del panel siempre se acotan a este
 *    panel (`stopPropagation`), para que un `Tab` en un panel anidado no cierre
 *    a un ancestro; solo cierran ESTE panel si es opt-in (`shouldCloseOnTabOut`,
 *    patrón WAI-ARIA APG *menu button*). En ese caso no se hace `preventDefault`:
 *    el foco sigue su curso natural, sin devolverlo al trigger. Con
 *    `shouldCloseOnTabLeave`, en cambio, el Tab que saldría del panel por su
 *    borde lo cierra y devuelve el foco al trigger, como Escape.
 *  - El cierre por click fuera NO mueve el foco (el foco sigue al click), y se
 *    ignoran los clicks dentro del ancla: el `onClick` del trigger ya alterna
 *    el estado, y sin este guard un click en el trigger durante la transición
 *    de salida reabriría y cerraría el panel en el mismo click (queda atascado).
 *  - Con `prefers-reduced-motion`, la transición `Grow` se anula (`timeout` 0).
 *  - Los listeners se limpian al desmontar o cuando cambia `open`.
 */
export function usePopoverPanel({
  anchorEl,
  handleClose,
  open,
  shouldCloseOnTabLeave = false,
  shouldCloseOnTabOut = false,
  shouldFocusOnOpen = true,
}: UsePopoverPanelParams): UsePopoverPanelReturn {
  useEffect(() => {
    if (!open || !handleClose) return undefined;

    function onEscape(): void {
      handleClose?.();
      focusAnchor(anchorEl);
    }

    return pushEscapeHandler(onEscape);
  }, [anchorEl, handleClose, open]);

  const onClickAway = useCallback(
    (event: ReactMouseEvent<Document>): void => {
      if (anchorEl instanceof HTMLElement && event.target instanceof Node && anchorEl.contains(event.target)) {
        return;
      }
      handleClose?.();
    },
    [anchorEl, handleClose],
  );

  // Manejo de `Tab`/`Shift+Tab` con el foco dentro del panel. Cualquier `Tab` se
  // acota SIEMPRE al panel más interno con `stopPropagation`, con independencia
  // de `shouldCloseOnTabOut`: como los eventos sintéticos de React burbujean por
  // el árbol de componentes (no el DOM del portal), un `Tab` ocurrido en un panel
  // descendiente llegaría al `onKeyDown` de un `Popover` ancestro; sin frenarlo,
  // un ancestro que sí cierra al tabular (`shouldCloseOnTabOut`) se cerraría por
  // un `Tab` que no le pertenece — el bug que colapsaba el formulario anidado de
  // "asignar alumnos". Misma semántica "solo el tope" que la pila de Escape.
  //
  // El cierre en sí solo ocurre si ESTE panel es opt-in (`shouldCloseOnTabOut`,
  // patrón APG *menu button*, los menús). NO se hace `preventDefault`: el foco
  // debe seguir su curso natural (siguiente/anterior tabbable) tanto al cerrar
  // como al navegar un formulario, sin atraparlo ni devolverlo al trigger (eso es
  // exclusivo de Escape).
  const container = getDialogContainer(anchorEl);
  const isInsideDialog = container !== undefined;

  const onPanelKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>): void => {
      // Escape con el foco DENTRO del panel se resuelve aquí y no espera al listener
      // de `document`: los eventos sintéticos de React se despachan en el contenedor
      // raíz, antes de que el nativo llegue a `document`, así que un ancestro que
      // también escucha Escape en React (el `Modal` de MUI cuando el panel se
      // portaliza dentro de un `Dialog`, vía `container`) lo vería primero y
      // cerraría el diálogo entero. Se delega en la misma pila que `document`: con
      // paneles anidados, Escape cierra el superior, no el que contiene el foco.
      if (event.key === ESCAPE_KEY) {
        closeTopmostPanel(event);
        return;
      }

      if (event.key !== TAB_KEY) return;
      event.stopPropagation();
      if (isInsideDialog && handleClose && isTabLeavingPanel(event.currentTarget, event.target, event.shiftKey)) {
        handleClose();
        if (event.shiftKey) event.preventDefault();
        focusAnchor(anchorEl);
        return;
      }
      if (shouldCloseOnTabOut) {
        handleClose?.();
        return;
      }
      // Opt-in: solo se intercepta el Tab que saldría del panel por su borde (el resto navega).
      if (!shouldCloseOnTabLeave || !isTabLeavingPanel(event.currentTarget, event.target, event.shiftKey)) return;
      event.preventDefault();
      handleClose?.();
      focusAnchor(anchorEl);
    },
    [anchorEl, handleClose, isInsideDialog, shouldCloseOnTabLeave, shouldCloseOnTabOut],
  );

  // Ref-callback del contenedor del panel: Popper monta el panel al abrirse,
  // así que el attach del nodo ES la apertura. Se enfoca el contenedor (no el
  // primer control) para no asumir contenido; `preventScroll` evita saltos.
  const panelRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (node && shouldFocusOnOpen && !node.contains(document.activeElement)) {
        node.focus({ preventScroll: true });
      }
    },
    [shouldFocusOnOpen],
  );

  const transitionTimeout = prefersReducedMotion() ? 0 : undefined;

  return { container, getGrowStyle, onClickAway, onPanelKeyDown, panelRef, transitionTimeout };
}

/**
 * Hook público del Popover v2 — patrón canónico de consumo.
 *
 * Gestiona su propio estado de apertura y la referencia al trigger. Esparce
 * `getTriggerProps()` sobre el elemento disparador para conectar ancla,
 * `aria-expanded`/`aria-haspopup` y el `onClick` de toggle.
 *
 * `hasPopup` controla el `aria-haspopup` del trigger (default `false` —
 * disclosure, solo `aria-expanded`; usar `'menu'` únicamente si el contenido
 * es un menú real y `'dialog'` si el panel contiene formularios).
 *
 * `shouldOpenOnArrowDown` (default `false`) añade al trigger la apertura con
 * `ArrowDown` del patrón APG *menu button*; el foco a la primera fila lo pone
 * el menú al montarse (`shouldAutoFocusFirstItem`).
 *
 * El componente `Popover` también funciona de forma autónoma con `anchorEl` y
 * `open` gestionados externamente (consumidores v1), sin necesidad de este hook.
 *
 * Gestión de foco (dos cierres distintos, patrón WAI-ARIA APG):
 *  - `close()`: cierre "intencionado" desde el contenido (p. ej. al seleccionar
 *    un item de menú). Devuelve el foco al trigger, como exige APG al activar
 *    una opción.
 *  - `dismiss()`: cierre por descarte del panel; es el que se conecta al
 *    `handleClose` del `Popover` (click-away + base del Escape). NO mueve el
 *    foco: el click-away no debe robarlo (queda en el elemento pulsado) y el
 *    Escape lo devuelve por su cuenta dentro del propio panel.
 * Por eso NUNCA conectes `close` a `handleClose`: robaría el foco en el
 * click-away.
 */
export function usePopover({ hasPopup = false, shouldOpenOnArrowDown = false }: UsePopoverParams = {}): UsePopoverReturn {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const triggerRef = useCallback((node: HTMLElement | null) => {
    setAnchorEl(node);
  }, []);

  const open = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const close = useCallback((): void => {
    setIsOpen(false);
    focusAnchor(anchorEl);
  }, [anchorEl]);

  const dismiss = useCallback((): void => {
    setIsOpen(false);
  }, []);

  const toggle = useCallback((): void => {
    setIsOpen((previous) => !previous);
  }, []);

  // `preventDefault` para que la flecha no desplace la página al abrir.
  const onTriggerKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>): void => {
    if (event.key !== ARROW_DOWN_KEY) return;
    event.preventDefault();
    setIsOpen(true);
  }, []);

  const getTriggerProps = useCallback(
    (): PopoverTriggerProps => ({
      'aria-expanded': isOpen,
      'aria-haspopup': hasPopup,
      onClick: toggle,
      ...(shouldOpenOnArrowDown ? { onKeyDown: onTriggerKeyDown } : {}),
      ref: triggerRef,
    }),
    [hasPopup, isOpen, onTriggerKeyDown, shouldOpenOnArrowDown, toggle, triggerRef],
  );

  return {
    anchorEl,
    close,
    dismiss,
    getTriggerProps,
    isOpen,
    open,
    toggle,
  };
}
